
import { supabase } from "@/integrations/supabase/client";

export const uploadMedia = async (files: File[], businessId: string) => {
  const mediaUrls: string[] = [];
  const uploadErrors: string[] = [];

  // Early validation
  if (!files.length) {
    return { mediaUrls: [], uploadErrors: [] };
  }

  if (!businessId) {
    console.error("No business ID provided for media upload");
    return {
      mediaUrls: [],
      uploadErrors: ["Invalid business ID for media upload"],
    };
  }

  console.log(
    `Starting upload of ${files.length} files for business ID ${businessId}`
  );

  try {
    // Quick bucket accessibility check
    const { data: listData, error: listError } = await supabase.storage
      .from("business-media")
      .list("", { limit: 1 });

    if (listError) {
      console.error("Cannot access business-media bucket:", listError);
      return {
        mediaUrls: [],
        uploadErrors: [
          "Storage is not accessible at the moment. Please try again later.",
        ],
      };
    }

    // Log the path we're going to use
    console.log(`Will upload files to path: ${businessId}/`);

    for (const file of files) {
      try {
        // Validate file size (5MB limit)
        const MAX_SIZE = 5 * 1024 * 1024; // 5MB
        if (file.size > MAX_SIZE) {
          uploadErrors.push(`${file.name} exceeds the 5MB size limit`);
          continue;
        }

        // Validate file type
        const allowedTypes = [
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
          "video/mp4",
          "video/quicktime",
        ];
        if (!allowedTypes.includes(file.type)) {
          uploadErrors.push(`${file.name} is not an allowed file type`);
          continue;
        }

        const fileExt = file.name.split(".").pop();
        // Use a UUID-style name to avoid collisions
        const uniqueId = `${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 15)}`;
        const fileName = `${businessId}/${uniqueId}.${fileExt}`;

        console.log(
          `Uploading file: ${file.name} (${(file.size / (1024 * 1024)).toFixed(
            2
          )} MB) of type ${file.type} to path: ${fileName}`
        );

        const { error: uploadError } = await supabase.storage
          .from("business-media")
          .upload(fileName, file, {
            cacheControl: "3600",
            upsert: true,
            contentType: null,
          });

        if (uploadError) {
          console.error("Error uploading file:", file.name, uploadError);
          uploadErrors.push(
            `Failed to upload ${file.name}: ${uploadError.message}`
          );
          continue;
        }

        // Get the public URL using the newer method
        const { data } = supabase.storage
          .from("business-media")
          .getPublicUrl(fileName);

        if (data?.publicUrl) {
          mediaUrls.push(data.publicUrl);
          console.log(`File uploaded successfully with URL: ${data.publicUrl}`);
        } else {
          console.warn(`Could not generate public URL for ${fileName}`);
          uploadErrors.push(`Could not generate public URL for ${file.name}`);
        }
      } catch (error: any) {
        console.error(`Unexpected error uploading ${file.name}:`, error);
        uploadErrors.push(
          `Failed to upload ${file.name}: ${
            error?.message || "Unexpected error"
          }`
        );
      }
    }

    console.log(
      `Upload process completed with ${mediaUrls.length} successful uploads and ${uploadErrors.length} failures`
    );

    return { mediaUrls, uploadErrors };
  } catch (error: any) {
    console.error("Fatal error during upload process:", error);
    return {
      mediaUrls: [],
      uploadErrors: [
        `Upload process failed: ${error?.message || "Unknown error"}`,
      ],
    };
  }
};
